import { c as _c } from "react/compiler-runtime";
import { MenuItem, Select } from '@mui/material';
import { ConditionOperatorEnum, type Condition } from '@repo/api-gw-sdk';
import { type FilterProperty, PropertyPolicyRelation } from '@/types/advanceFilter';
import { inlineDropdownStyle } from './inlineDropdownStyle';
export const OPERATORS: Record<PropertyPolicyRelation, Partial<Record<ConditionOperatorEnum, {
  label: string;
}>>> = {
  [PropertyPolicyRelation.SingleValue]: {
    [ConditionOperatorEnum.In]: {
      label: 'is one of'
    },
    [ConditionOperatorEnum.NotIn]: {
      label: 'is not one of'
    }
  },
  [PropertyPolicyRelation.SingleValueIncludeOnly]: {
    [ConditionOperatorEnum.In]: {
      label: 'is one of'
    }
  },
  [PropertyPolicyRelation.MultipleValues]: {
    [ConditionOperatorEnum.ContainsAnyOf]: {
      label: 'contains any of'
    },
    [ConditionOperatorEnum.ContainsAllOf]: {
      label: 'contains all of'
    },
    [ConditionOperatorEnum.ContainsNoneOf]: {
      label: 'contains none of'
    }
  },
  [PropertyPolicyRelation.String]: {
    [ConditionOperatorEnum.Contains]: {
      label: 'contains'
    },
    [ConditionOperatorEnum.NotContains]: {
      label: 'does not contain'
    },
    [ConditionOperatorEnum.StartsWith]: {
      label: 'starts with'
    },
    [ConditionOperatorEnum.NotStartsWith]: {
      label: 'does not start with'
    },
    [ConditionOperatorEnum.EndsWith]: {
      label: 'ends with'
    },
    [ConditionOperatorEnum.NotEndsWith]: {
      label: 'does not end with'
    }
  },
  [PropertyPolicyRelation.Contains]: {
    [ConditionOperatorEnum.Contains]: {
      label: 'contains'
    }
  },
  [PropertyPolicyRelation.SingleValueOrString]: {
    [ConditionOperatorEnum.In]: {
      label: 'is one of'
    },
    [ConditionOperatorEnum.NotIn]: {
      label: 'is not one of'
    },
    [ConditionOperatorEnum.Contains]: {
      label: 'contains'
    },
    [ConditionOperatorEnum.NotContains]: {
      label: 'does not contain'
    },
    [ConditionOperatorEnum.StartsWith]: {
      label: 'starts with'
    },
    [ConditionOperatorEnum.NotStartsWith]: {
      label: 'does not start with'
    },
    [ConditionOperatorEnum.EndsWith]: {
      label: 'ends with'
    },
    [ConditionOperatorEnum.NotEndsWith]: {
      label: 'does not end with'
    }
  },
  [PropertyPolicyRelation.Numeric]: {
    [ConditionOperatorEnum.GreaterThan]: {
      label: 'is greater than'
    },
    [ConditionOperatorEnum.GreaterThanOrEquals]: {
      label: 'is greater than or equal to'
    },
    [ConditionOperatorEnum.LessThan]: {
      label: 'is less than'
    },
    [ConditionOperatorEnum.LessThanOrEquals]: {
      label: 'is less than or equal to'
    },
    [ConditionOperatorEnum.Equals]: {
      label: 'is'
    },
    [ConditionOperatorEnum.NotEquals]: {
      label: 'is not'
    }
  }
};

/**
 * The operator a condition opens on, per relation. Every relation lists it first
 * in {@link OPERATORS} too, but stating it here keeps the choice out of the hands
 * of object key order — and makes adding a relation a type error until its
 * default is named.
 */
const DEFAULT_OPERATORS: Record<PropertyPolicyRelation, ConditionOperatorEnum> = {
  [PropertyPolicyRelation.SingleValue]: ConditionOperatorEnum.In,
  [PropertyPolicyRelation.SingleValueOrString]: ConditionOperatorEnum.In,
  [PropertyPolicyRelation.SingleValueIncludeOnly]: ConditionOperatorEnum.In,
  [PropertyPolicyRelation.MultipleValues]: ConditionOperatorEnum.ContainsAnyOf,
  [PropertyPolicyRelation.String]: ConditionOperatorEnum.Contains,
  [PropertyPolicyRelation.Contains]: ConditionOperatorEnum.Contains,
  [PropertyPolicyRelation.Numeric]: ConditionOperatorEnum.GreaterThan
};

/** Single-select is the default relation, so a property that names none matches on exact values. */
export const relationOf = (property: FilterProperty) => property.policyRelation ?? PropertyPolicyRelation.SingleValue;
export const defaultOperatorFor = (property: FilterProperty) => DEFAULT_OPERATORS[relationOf(property)];

/**
 * On a multi-value property `Contains` means "contains all of", and conditions
 * stored before that meaning had an operator of its own still spell it that way.
 * Both spellings resolve to the same backend operator — `AsOperator` folds
 * "Contains All Of" into `Contains` — so this renames a condition without
 * changing what it matches.
 *
 * Worth rewriting rather than just relabelling: the two spellings serialize to
 * different filter fields, and multi-value schemas define only `containsAllOf`,
 * so a condition left spelled the old way is dropped by the API and its filter
 * reads as applied while the list stays unfiltered.
 */
export const canonicalOperator = (operator: string | undefined, relation: PropertyPolicyRelation) => relation === PropertyPolicyRelation.MultipleValues && operator === ConditionOperatorEnum.Contains ? ConditionOperatorEnum.ContainsAllOf : operator ?? '';
interface OperatorSelectProps {
  condition: Condition;
  propertyRelation: PropertyPolicyRelation;
  onChange: (condition: Condition) => void;
}
export function OperatorSelect(t0) {
  const $ = _c(15);
  const {
    condition,
    propertyRelation,
    onChange
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      ...inlineDropdownStyle,
      height: "100%"
    };
    t2 = {
      disablePortal: true
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== condition.operator || $[3] !== propertyRelation) {
    t3 = canonicalOperator(condition.operator, propertyRelation);
    $[2] = condition.operator;
    $[3] = propertyRelation;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== condition || $[6] !== onChange || $[7] !== propertyRelation) {
    t4 = event => onChange({
      ...condition,
      operator: event.target.value,
      value: propertyRelation === PropertyPolicyRelation.Numeric ? condition.value : []
    });
    $[5] = condition;
    $[6] = onChange;
    $[7] = propertyRelation;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== propertyRelation) {
    t5 = Object.entries(OPERATORS[propertyRelation]).map(_temp);
    $[9] = propertyRelation;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t4 || $[13] !== t5) {
    t6 = <Select sx={t1} MenuProps={t2} value={t3} onChange={t4}>{t5}</Select>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
function _temp(t0) {
  const [key, value] = t0;
  return <MenuItem key={key} value={key}>{value.label}</MenuItem>;
}